/* eslint-disable consistent-return */
import { useCallback, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Container from '@material-ui/core/Container';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { format, parseISO, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import SearchIcon from '@material-ui/icons/Search';
import GetAppIcon from '@material-ui/icons/GetApp';
import FilterListIcon from '@material-ui/icons/FilterList';
import CancelIcon from '@material-ui/icons/Cancel';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import analyticsService from '../API/analyticsService';
import CatalogoService from '../API/CatalogoService';
import { showAlert } from '../redux/global/globalSlice';

const AnaliticasScreen = () => {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const { isLogged } = useAppSelector(({ adminReducer }) => adminReducer);

  const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split('T')[0]);
  const [fechaFin, setFechaFin] = useState(new Date().toISOString().split('T')[0]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [catalogoMetricas, setCatalogoMetricas] = useState([]);
  const [eventsData, setEventsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [searchDates, setSearchDates] = useState({
    start: new Date().toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });

  const buildParams = useCallback(() => {
    const params = {};
    if (fechaInicio) params.fechaInicio = fechaInicio;
    if (fechaFin) params.fechaFin = fechaFin;
    if (selectedEvents.length > 0) params.event = selectedEvents.join(',');
    return params;
  }, [fechaInicio, fechaFin, selectedEvents]);

  const fetchEvents = useCallback(
    async params => {
      setIsLoading(true);
      try {
        const resp = await analyticsService.getEvents(dispatch, params);
        setEventsData(Array.isArray(resp) ? resp : []);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error consultando eventos:', error);
        setEventsData([]);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const resp = await CatalogoService.consultaCatalogo({
          dispatch,
          path: 'metricas',
        });
        if (Array.isArray(resp)) {
          setCatalogoMetricas(resp);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error cargando catálogo de métricas:', error);
      }
    };
    loadCatalog();
    const today = new Date().toISOString().split('T')[0];
    fetchEvents({
      fechaInicio: today,
      fechaFin: today,
    });
    setSearchDates({ start: today, end: today });
  }, [dispatch, fetchEvents]);

  const canSearch = fechaInicio && fechaFin;

  const handleSearch = () => {
    if (!canSearch) return;
    fetchEvents(buildParams());
    setSearchDates({ start: fechaInicio, end: fechaFin });
  };

  const handleClear = () => {
    setFechaInicio('');
    setFechaFin('');
    setSelectedEvents([]);
    setEventsData([]);
    setSearchDates({ start: '', end: '' });
  };

  const handleDownloadExcel = async () => {
    setIsDownloading(true);
    try {
      const params = buildParams();
      await analyticsService.downloadEventsExcel(params);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error descargando reporte:', error);
      showAlert(dispatch, {
        message: 'Error al descargar el reporte.',
        type: 'ERROR',
        timeout: 6000,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const getEventName = id => {
    const found = catalogoMetricas.find(o => o.id === id);
    return found ? found.value : id;
  };

  const handleDeleteChip = idToDelete => {
    setSelectedEvents(prev => prev.filter(v => v !== idToDelete));
  };

  const formatDateRange = (start, end) => {
    if (!start || !end) return '—';
    const fmt = d => d.split('-').reverse().join('/');
    return `${fmt(start)} - ${fmt(end)}`;
  };

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <Box className={classes.emptyState}>
          <CircularProgress size={40} color="primary" />
          <Typography className={classes.emptyText}>Consultando eventos...</Typography>
        </Box>
      );
    }

    if (eventsData.length === 0) {
      return (
        <Box className={classes.emptyState}>
          <Box className={classes.emptyIcon}>
            <FilterListIcon style={{ fontSize: 32, color: '#8792a2' }} />
          </Box>
          <Typography className={classes.emptyTitle}>Sin resultados</Typography>
          <Typography className={classes.emptyText}>
            No se encontraron eventos para los filtros seleccionados.
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeadCell} style={{ width: '25%' }}>
                Fecha
              </TableCell>
              <TableCell className={classes.tableHeadCell} style={{ width: '55%' }}>
                Descripción
              </TableCell>
              <TableCell className={classes.tableHeadCell} align="right" style={{ width: '20%' }}>
                Interacciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {eventsData.map((row, index) => (
              <TableRow key={row.event || index} className={classes.tableRow}>
                <TableCell>
                  <Typography className={classes.eventName}>
                    {formatDateRange(searchDates.start, searchDates.end)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography className={classes.eventDescription}>{row.description || '—'}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography className={classes.eventTotal}>{(row.total || 0).toLocaleString('es-MX')}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (!isLogged) {
    return <Redirect to="/acceso/login" />;
  }

  return (
    <Container maxWidth="lg" className={classes.root}>
      <Paper className={classes.filtersCard} elevation={0}>
        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 16 }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <FilterListIcon style={{ fontSize: 16, color: '#8792a2' }} />
            <Typography className={classes.filtersTitle}>Filtros de búsqueda</Typography>
          </Box>
        </Box>

        <Box className={classes.filtersGrid}>
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={es}>
            <KeyboardDatePicker
              id="fechaInicio"
              label="Fecha Inicio"
              format="dd/MM/yyyy"
              value={fechaInicio ? parseISO(fechaInicio) : null}
              onChange={date => {
                if (date && isValid(date)) {
                  setFechaInicio(format(date, 'yyyy-MM-dd'));
                } else {
                  setFechaInicio('');
                }
              }}
              variant="inline"
              inputVariant="outlined"
              size="small"
              fullWidth
              autoOk
              KeyboardButtonProps={{
                'aria-label': 'cambiar fecha de inicio',
              }}
            />

            <KeyboardDatePicker
              id="fechaFin"
              label="Fecha Fin"
              format="dd/MM/yyyy"
              value={fechaFin ? parseISO(fechaFin) : null}
              onChange={date => {
                if (date && isValid(date)) {
                  setFechaFin(format(date, 'yyyy-MM-dd'));
                } else {
                  setFechaFin('');
                }
              }}
              variant="inline"
              inputVariant="outlined"
              size="small"
              fullWidth
              autoOk
              KeyboardButtonProps={{
                'aria-label': 'cambiar fecha de fin',
              }}
            />
          </MuiPickersUtilsProvider>

          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel id="eventos-label">Eventos</InputLabel>
            <Select
              labelId="eventos-label"
              id="eventos-select"
              multiple
              value={selectedEvents}
              onChange={e => setSelectedEvents(e.target.value)}
              label="Eventos"
              renderValue={selected => (
                <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 4 }}>
                  {selected.slice(0, 1).map(id => (
                    <Chip
                      key={id}
                      label={getEventName(id)}
                      size="small"
                      className={classes.chip}
                      onDelete={() => handleDeleteChip(id)}
                      deleteIcon={
                        <CancelIcon className={classes.chipDeleteIcon} onMouseDown={e => e.stopPropagation()} />
                      }
                    />
                  ))}
                  {selected.length > 1 && (
                    <Typography variant="caption" style={{ color: '#5a6577', fontWeight: 600 }}>
                      {`+${selected.length - 1}`}
                    </Typography>
                  )}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    borderRadius: 12,
                  },
                },
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                getContentAnchorEl: null,
              }}
            >
              {catalogoMetricas.map(opt => (
                <MenuItem
                  key={opt.id}
                  value={opt.id}
                  className={classes.menuItem}
                  style={selectedEvents.includes(opt.id) ? { fontWeight: 600, backgroundColor: '#fff7f0' } : {}}
                >
                  {opt.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box display="flex" style={{ gap: 8 }}>
            <Button
              variant="contained"
              disableElevation
              onClick={handleClear}
              disabled={isLoading}
              className={classes.btnSecondary}
              fullWidth
            >
              Limpiar Filtros
            </Button>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              onClick={handleSearch}
              disabled={isLoading || !canSearch}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              className={classes.btnPrimary}
              fullWidth
            >
              Buscar
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper className={classes.tableCard} elevation={0}>
        <Box className={classes.tableHeader}>
          <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 10 }} width="100%">
            <Typography className={classes.tableTitle}>
              <span style={{ color: '#ff6600', fontWeight: 700 }}>{eventsData.length}</span> Resultados
            </Typography>
            <Button
              variant="contained"
              disableElevation
              onClick={handleDownloadExcel}
              disabled={isDownloading || isLoading || !canSearch}
              startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
              className={classes.btnExcel}
              size="small"
            >
              {isDownloading ? 'Descargando...' : 'Descargar Excel'}
            </Button>
          </Box>
        </Box>
        {renderTableContent()}
      </Paper>
    </Container>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },

  filtersCard: {
    marginBottom: theme.spacing(2.5),
    backgroundColor: 'transparent',
  },
  filtersTitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#8792a2',
  },
  filtersGrid: {
    display: 'grid',
    gridTemplateColumns: '1.2fr 1.2fr 2fr 1.5fr',
    alignItems: 'center',
    gap: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      borderRadius: 50,
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },

  chip: {
    backgroundColor: '#fff',
    color: '#000',
    fontWeight: 600,
    fontSize: '0.72rem',
    border: '1px solid rgba(0, 0, 0, 0.15)',
    height: 24,
  },
  chipDeleteIcon: {
    color: '#000 !important',
    fontSize: '16px !important',
    '&:hover': {
      color: `${theme.palette.primary.main} !important`,
    },
  },
  menuItem: {
    fontSize: '0.875rem',
    '&:hover': {
      backgroundColor: '#fff7f0',
    },
  },

  btnPrimary: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 50,
    boxShadow: '0 2px 8px rgba(255, 102, 0, 0.25)',
    '&:hover': {
      boxShadow: '0 4px 14px rgba(255, 102, 0, 0.35)',
    },
  },
  btnSecondary: {
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: 50,
    backgroundColor: '#353535',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#222',
    },
  },
  btnExcel: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 50,
    backgroundColor: 'transparent',
    color: '#16a34a',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },

  tableCard: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2.5),
    borderBottom: '1px solid #eef0f4',
  },
  tableTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#1a1f36',
    textAlign: 'left',
  },
  resultBadge: {
    backgroundColor: '#fff3eb',
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: '0.72rem',
    border: '1px solid rgba(255, 102, 0, 0.12)',
    height: 22,
  },

  table: {
    '& .MuiTableCell-root': {
      borderBottom: '1px solid #f2f4f7',
    },
  },
  tableHeadCell: {
    fontWeight: 900,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#fff',
    backgroundColor: '#ff6600',
    whiteSpace: 'nowrap',
  },
  tableRow: {
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#f8f9fd',
    },
  },
  eventName: {
    fontWeight: 400,
    color: '#1a1f36',
    fontSize: '0.875rem',
  },
  eventDescription: {
    color: '#1a1f36',
    fontSize: '0.9rem',
    fontWeight: 400,
  },
  eventTotal: {
    fontSize: '1.1rem',
    fontWeight: 400,
    color: '#1a1f36',
    fontVariantNumeric: 'tabular-nums',
  },

  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6, 3),
    textAlign: 'center',
  },
  emptyIcon: {
    width: 64,
    height: 64,
    borderRadius: 16,
    backgroundColor: '#f0f2f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1.5),
  },
  emptyTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#5a6577',
    marginBottom: 4,
  },
  emptyText: {
    fontSize: '0.82rem',
    color: '#8792a2',
  },
}));

export default AnaliticasScreen;
